import { useState, type KeyboardEvent } from 'react';
import { Plus } from 'lucide-react';

/**
 * Adds a line to a sheet section (no AI call): a small « + » opens a field,
 * Enter adds, Escape or an empty field cancels. Domino reads the sheet as it
 * stands at its next answer.
 */
export const AddLine = ({ label, placeholder, onAdd }: {
  /** What the button adds, for screen readers ("Ajouter une entrée"). */
  label: string;
  placeholder: string;
  onAdd: (text: string) => void;
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');

  const close = () => {
    setEditing(false);
    setDraft('');
  };
  const save = () => {
    const next = draft.trim();
    close();
    if (next) onAdd(next);
  };
  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') close();
    else if (event.key === 'Enter') {
      event.preventDefault();
      save();
    }
  };

  if (!editing) {
    return <button type="button" aria-label={label} title={label}
      className="flex items-center gap-1 rounded text-sm text-domino-inkSoft transition-[color,transform] duration-150 ease-out hover:text-domino-aubergine active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-domino-prune"
      onClick={() => setEditing(true)}>
      <Plus className="h-3.5 w-3.5" aria-hidden="true" />
      Ajouter
    </button>;
  }

  return <input
    autoFocus
    aria-label={label}
    value={draft}
    placeholder={placeholder}
    onChange={(event) => setDraft(event.target.value)}
    onBlur={save}
    onKeyDown={onKeyDown}
    className="w-full rounded-md border border-domino-cardBorder bg-domino-white px-2 py-1 text-sm text-domino-ink placeholder:text-domino-inkSoft focus:outline-none focus:ring-2 focus:ring-domino-prune"
  />;
};

/** « Ville : Lyon » → label and value; without a colon, the value stays to fill. */
export const splitLabelValue = (text: string): { label: string; value: string } => {
  const colon = text.indexOf(':');
  if (colon <= 0) return { label: text, value: '' };
  return { label: text.slice(0, colon).trim(), value: text.slice(colon + 1).trim() };
};
